import apiV2 from "api-v2";
import type {
	RfxQuoteTable,
	RfxQuoteItem,
	RfxQuoteColumn,
	RfxQuoteColumnType,
} from "../Submission/types/quote.type";

export type QuoteResourceType = "rfx" | "rfx-template" | "quote-settings";

export interface CreateQuoteTablePayload {
	name: string;
	columns?: {
		column_type: string;
		name: string;
		sort_order: number;
	}[];
	default_rows?: {
		unit_id?: number | null;
		/**
		 * Per-cell values keyed by the column's `sort_order`. Description text
		 * for any description column lives here (cells-only model — there is no
		 * `name` slot on rows).
		 */
		cells?: Record<number, string>;
		sort_order: number;
	}[];
	description?: string;
	required?: boolean;
	sort_order?: number;
}

export interface UpdateQuoteTablePayload {
	name?: string;
	description?: string;
	include_totals_row?: boolean;
	required?: boolean;
	sort_order?: number;
}

export interface ReorderQuoteTablesPayload {
	quote_ids: number[];
}

export interface CreateQuoteRowPayload {
	name: string;
	sort_order?: number;
	unit_id: number | null;
}

export interface UpdateQuoteRowPayload {
	name?: string;
	sort_order?: number;
	unit_id?: number | null;
}

export interface ReorderQuoteRowsPayload {
	row_ids: number[];
}

export interface CreateColumnPayload {
	name: string;
	column_type: RfxQuoteColumnType;
}

export interface UpdateColumnPayload {
	name?: string;
	sort_order?: number;
}

export interface ReorderColumnsPayload {
	column_ids: number[];
}

const buildTablePath = (
	rfxId: number,
	resourceType: QuoteResourceType = "rfx"
): string => `${resourceType}/${rfxId}/quotes`;
const buildRowPath = (
	rfxId: number,
	quoteId: number,
	resourceType: QuoteResourceType = "rfx"
): string => `${resourceType}/${rfxId}/quotes/${quoteId}/rows`;
const buildColumnPath = (
	rfxId: number,
	quoteId: number,
	resourceType: QuoteResourceType = "rfx"
): string => `${resourceType}/${rfxId}/quotes/${quoteId}/columns`;

// --- Quote Settings ---

export interface QuoteSettings {
	allow_flexible_quote?: boolean;
	quote_file_path?: { [key: string]: unknown; file_path: string } | null;
	quote_instructions?: string | null;
	quote_type?: "form" | "file";
}

interface ApiResponseWithAdditional<T> {
	additional?: { is_quote_tab_valid?: boolean };
	data: T;
}

export const updateQuoteSettings = async (
	rfxId: number,
	payload: QuoteSettings,
	resourceType: QuoteResourceType = "rfx"
): Promise<QuoteSettings> => {
	const response = await apiV2.patch<ApiResponseWithAdditional<QuoteSettings>>(
		`${buildTablePath(rfxId, resourceType)}/settings`,
		payload
	);
	return response.data.data;
};

// --- Quote Tables ---

export const getQuoteTables = async (
	rfxId: number,
	resourceType: QuoteResourceType = "rfx"
): Promise<RfxQuoteTable[]> => {
	const response = await apiV2.get<{ data: RfxQuoteTable[] }>(
		buildTablePath(rfxId, resourceType)
	);
	return response.data.data;
};

export const createQuoteTable = async (
	rfxId: number,
	payload: CreateQuoteTablePayload,
	resourceType: QuoteResourceType = "rfx"
): Promise<RfxQuoteTable> => {
	const response = await apiV2.post<{ data: RfxQuoteTable }>(
		buildTablePath(rfxId, resourceType),
		payload
	);
	return response.data.data;
};

export const updateQuoteTable = async (
	rfxId: number,
	quoteId: number,
	payload: UpdateQuoteTablePayload,
	resourceType: QuoteResourceType = "rfx"
): Promise<RfxQuoteTable> => {
	const response = await apiV2.patch<{ data: RfxQuoteTable }>(
		`${buildTablePath(rfxId, resourceType)}/${quoteId}`,
		payload
	);
	return response.data.data;
};

export const deleteQuoteTable = async (
	rfxId: number,
	quoteId: number,
	resourceType: QuoteResourceType = "rfx"
): Promise<void> => {
	await apiV2.delete(`${buildTablePath(rfxId, resourceType)}/${quoteId}`);
};

export const reorderQuoteTables = async (
	rfxId: number,
	payload: ReorderQuoteTablesPayload,
	resourceType: QuoteResourceType = "rfx"
): Promise<void> => {
	await apiV2.post(`${buildTablePath(rfxId, resourceType)}/reorder`, payload);
};

export const copyQuoteTable = async (
	rfxId: number,
	quoteId: number,
	resourceType: QuoteResourceType = "rfx"
): Promise<RfxQuoteTable> => {
	const response = await apiV2.post<{ data: RfxQuoteTable }>(
		`${buildTablePath(rfxId, resourceType)}/${quoteId}/copy`
	);
	return response.data.data;
};

// --- Quote Rows ---

export const getQuoteRows = async (
	rfxId: number,
	quoteId: number,
	resourceType: QuoteResourceType = "rfx"
): Promise<RfxQuoteItem[]> => {
	const response = await apiV2.get<{ data: RfxQuoteItem[] }>(
		buildRowPath(rfxId, quoteId, resourceType)
	);
	return response.data.data;
};

export const createQuoteRow = async (
	rfxId: number,
	quoteId: number,
	payload: CreateQuoteRowPayload,
	resourceType: QuoteResourceType = "rfx"
): Promise<RfxQuoteItem> => {
	const response = await apiV2.post<{ data: RfxQuoteItem }>(
		buildRowPath(rfxId, quoteId, resourceType),
		payload
	);
	return response.data.data;
};

export const updateQuoteRow = async (
	rfxId: number,
	quoteId: number,
	rowId: number,
	payload: UpdateQuoteRowPayload,
	resourceType: QuoteResourceType = "rfx"
): Promise<RfxQuoteItem> => {
	const response = await apiV2.patch<{ data: RfxQuoteItem }>(
		`${buildRowPath(rfxId, quoteId, resourceType)}/${rowId}`,
		payload
	);
	return response.data.data;
};

export const deleteQuoteRow = async (
	rfxId: number,
	quoteId: number,
	rowId: number,
	resourceType: QuoteResourceType = "rfx"
): Promise<void> => {
	await apiV2.delete(`${buildRowPath(rfxId, quoteId, resourceType)}/${rowId}`);
};

export interface BatchCreateRowPayload {
	unit_id?: number | null;
	sort_order?: number;
	cells?: Array<{ column_id: number; value: string }>;
}

export const batchCreateQuoteRows = async (
	rfxId: number,
	quoteId: number,
	rows: BatchCreateRowPayload[],
	resourceType: QuoteResourceType = "rfx"
): Promise<RfxQuoteItem[]> => {
	const response = await apiV2.post<{ data: RfxQuoteItem[] }>(
		`${buildRowPath(rfxId, quoteId, resourceType)}/batch`,
		{ rows }
	);
	return response.data.data;
};

export const batchDeleteQuoteRows = async (
	rfxId: number,
	quoteId: number,
	rowIds: number[],
	resourceType: QuoteResourceType = "rfx"
): Promise<void> => {
	await apiV2.post(`${buildRowPath(rfxId, quoteId, resourceType)}/batch-destroy`, { row_ids: rowIds });
};

export interface BatchUpdateRowPayload {
	id: number;
	name?: string;
	unit_id?: number | null;
}

export const batchUpdateQuoteRows = async (
	rfxId: number,
	quoteId: number,
	rows: BatchUpdateRowPayload[],
	resourceType: QuoteResourceType = "rfx"
): Promise<boolean | undefined> => {
	const response = await apiV2.patch<ApiResponseWithAdditional<unknown>>(
		`${buildRowPath(rfxId, quoteId, resourceType)}/batch`,
		{ rows }
	);
	return response.data.additional?.is_quote_tab_valid;
};

export const reorderQuoteRows = async (
	rfxId: number,
	quoteId: number,
	payload: ReorderQuoteRowsPayload,
	resourceType: QuoteResourceType = "rfx"
): Promise<void> => {
	await apiV2.post(
		`${buildRowPath(rfxId, quoteId, resourceType)}/reorder`,
		payload
	);
};

// --- Quote Columns ---

export const getQuoteColumns = async (
	rfxId: number,
	quoteId: number,
	resourceType: QuoteResourceType = "rfx"
): Promise<RfxQuoteColumn[]> => {
	const response = await apiV2.get<{ data: RfxQuoteColumn[] }>(
		buildColumnPath(rfxId, quoteId, resourceType)
	);
	return response.data.data;
};

export const createQuoteColumn = async (
	rfxId: number,
	quoteId: number,
	payload: CreateColumnPayload,
	resourceType: QuoteResourceType = "rfx"
): Promise<RfxQuoteColumn> => {
	const response = await apiV2.post<{ data: RfxQuoteColumn }>(
		buildColumnPath(rfxId, quoteId, resourceType),
		payload
	);
	return response.data.data;
};

export const updateQuoteColumn = async (
	rfxId: number,
	quoteId: number,
	columnId: number,
	payload: UpdateColumnPayload,
	resourceType: QuoteResourceType = "rfx"
): Promise<RfxQuoteColumn> => {
	const response = await apiV2.patch<{ data: RfxQuoteColumn }>(
		`${buildColumnPath(rfxId, quoteId, resourceType)}/${columnId}`,
		payload
	);
	return response.data.data;
};

export const deleteQuoteColumn = async (
	rfxId: number,
	quoteId: number,
	columnId: number,
	resourceType: QuoteResourceType = "rfx"
): Promise<void> => {
	await apiV2.delete(
		`${buildColumnPath(rfxId, quoteId, resourceType)}/${columnId}`
	);
};

export const reorderQuoteColumns = async (
	rfxId: number,
	quoteId: number,
	payload: ReorderColumnsPayload,
	resourceType: QuoteResourceType = "rfx"
): Promise<void> => {
	await apiV2.post(
		`${buildColumnPath(rfxId, quoteId, resourceType)}/reorder`,
		payload
	);
};

// --- Existing Categories (cross-table) ---

export interface ExistingValueColumnsResponse {
	quoteId: number;
	quoteName: string;
	valueColumns: string[];
}

export const getExistingValueColumns = async (
	rfxId: number,
	resourceType: QuoteResourceType = "rfx"
): Promise<ExistingValueColumnsResponse[]> => {
	const response = await apiV2.get<{ data: ExistingValueColumnsResponse[] }>(
		`${buildTablePath(rfxId, resourceType)}/columns/existing-value-columns`
	);
	return response.data.data;
};

// --- Sync Layout (single-call table update) ---

export interface SyncLayoutColumnPayload {
	id?: number | null;
	name: string;
	column_type?: string;
	sort_order?: number;
}

export interface SyncLayoutRowPayload {
	id?: number | null;
	name?: string;
	sort_order?: number;
	unit_id?: number | null;
}

export interface SyncLayoutPayload {
	name?: string;
	columns?: SyncLayoutColumnPayload[];
	description?: string;
	rows?: SyncLayoutRowPayload[];
}

export const syncQuoteTableLayout = async (
	rfxId: number,
	quoteId: number,
	payload: SyncLayoutPayload,
	resourceType: QuoteResourceType = "rfx"
): Promise<RfxQuoteTable> => {
	const response = await apiV2.put<{ data: RfxQuoteTable }>(
		`${buildTablePath(rfxId, resourceType)}/${quoteId}/sync-layout`,
		payload
	);
	return response.data.data;
};

// --- Cell batch update (procurer-editable columns) ---

export interface CellUpdatePayload {
	column_id: number;
	row_id: number;
	value: string | null;
}

export const batchUpdateCells = async (
	rfxId: number,
	quoteId: number,
	cells: CellUpdatePayload[],
	resourceType: QuoteResourceType = "rfx"
): Promise<boolean | undefined> => {
	const response = await apiV2.patch<ApiResponseWithAdditional<unknown>>(
		`${buildTablePath(rfxId, resourceType)}/${quoteId}/cells`,
		{ cells }
	);
	return response.data.additional?.is_quote_tab_valid;
};

// --- Save as Template ---

export const saveQuoteAsTemplate = async (
	rfxId: number,
	quoteId: number,
	name: string,
	resourceType: QuoteResourceType = "rfx"
): Promise<void> => {
	await apiV2.post(
		`${buildTablePath(rfxId, resourceType)}/${quoteId}/save-as-template`,
		{ name }
	);
};

// --- Create table from template ---

export const createQuoteTableFromTemplate = async (
	rfxId: number,
	templateId: number,
	resourceType: QuoteResourceType = "rfx"
): Promise<RfxQuoteTable> => {
	const response = await apiV2.post<{ data: RfxQuoteTable }>(
		`${buildTablePath(rfxId, resourceType)}/from-template/${templateId}`
	);
	return response.data.data;
};

// --- Tab validation status ---

export interface TabStatusPayload {
	is_valid: boolean;
	tab: "quote" | "documentation" | "questionnaire" | "suppliers";
}

export interface TabStatusResponse {
	is_documentation_tab_valid: boolean;
	is_questionnaire_tab_valid: boolean;
	is_quote_tab_valid: boolean;
	is_suppliers_tab_valid: boolean;
}

export const updateTabStatus = async (
	rfxId: number,
	payload: TabStatusPayload
): Promise<TabStatusResponse> => {
	const response = await apiV2.put<{ data: TabStatusResponse }>(
		`rfx/${rfxId}/tab-status`,
		payload
	);
	return response.data.data;
};
